'use client';

import './date-field.scss';

import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '@radix-ui/react-popover';
import { FC, useEffect, useState } from 'react';
import { DateLib, Locale, Matcher } from 'react-day-picker';
import { enUS, es, ko } from 'react-day-picker/locale';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { LOCALES_CODES } from '@sola/contentful-model/lib/types.js';
import { i18nConfig } from '@sola/schema/lib/i18n-config.js';
import { toIsoDateString } from '@sola/utils/date.js';

import { useDateMask } from '../../utils/masks';
import { useDatePattern } from '../../utils/patterns';
import { Calendar } from '../calendar/calendar';
import { FieldIconButton } from '../field/field';
import { InputField, InputFieldProps } from '../input-field/input-field';

type DateFieldProps = InputFieldProps & {
  // The date to pre-select.
  defaultDate?: Date;
  // The number of days from the current day for which dates are allowed.
  minDaysOut?: number;
  // The maximum number of years shown in the calendar dropdown.
  maxYears?: number;
  shouldSetInitialDate?: boolean;
};

// TODO: Should we lazy load these react-day-picker locales? Maybe if we add more languages.
const localeMap = {
  [LOCALES_CODES.enum.en]: enUS,
  [LOCALES_CODES.enum.es]: es,
  [LOCALES_CODES.enum.ko]: ko,
};

const formatDate = (date: Date | undefined, locale: Locale) => {
  if (!date) {
    return '';
  }

  const dateLib = new DateLib({ locale });
  return dateLib.format(date, 'P');
};

const isValidDate = (date?: Date) => {
  if (!date) {
    return;
  }
  return !Number.isNaN(date.getTime());
};

const getDate = (val: string) => {
  const date = new Date(val);
  if (isValidDate(date)) {
    return date;
  }
};

export const DateField: FC<DateFieldProps> = ({
  name,
  minDaysOut,
  defaultDate = new Date(),
  maxYears = 10,
  shouldSetInitialDate = true,
  ...inputFieldProps
}) => {
  const { i18n, t } = useTranslation();
  const { trigger, register, setValue: setFormValue } = useFormContext();
  const disableMatchers: Matcher[] = [];
  const hiddenFieldName = `${name}Date`;
  const currentLocaleString =
    i18n.language in localeMap ? i18n.language : i18nConfig.defaultLocale;
  const currentLocale =
    localeMap[currentLocaleString as keyof typeof localeMap];

  let endDate: Date | undefined;
  let firstAllowedDate: Date | undefined;

  if (minDaysOut) {
    firstAllowedDate = new Date();
    firstAllowedDate.setDate(firstAllowedDate.getDate() + minDaysOut);
  }

  const startDate =
    firstAllowedDate && firstAllowedDate > defaultDate
      ? firstAllowedDate
      : defaultDate;

  if (firstAllowedDate) {
    disableMatchers.push({ before: firstAllowedDate });
  }

  if (maxYears) {
    endDate = new Date(startDate);
    endDate.setFullYear(endDate.getFullYear() + maxYears);
  }

  const { mask, loadMask } = useDateMask(currentLocaleString);
  const { pattern, loadPattern } = useDatePattern(currentLocaleString);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [date, setDate] = useState<Date | undefined>(startDate);
  const [month, setMonth] = useState<Date | undefined>(startDate);
  const [value, setValue] = useState(
    shouldSetInitialDate ? formatDate(startDate, currentLocale) : '',
  );

  useEffect(() => {
    if (!name || !shouldSetInitialDate) {
      return;
    }
    trigger(name);
  }, [value, name]);

  useEffect(() => {
    // Set the hidden input value when date changes
    setFormValue(hiddenFieldName, date?.toString());
  }, [date, setFormValue]);

  return (
    <div className="DateField">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <input type="hidden" {...register(hiddenFieldName)} />
        <InputField
          name={name}
          value={value}
          type="text"
          inputMode="numeric"
          footer={<PopoverAnchor />}
          iconElement={
            <PopoverTrigger asChild>
              <FieldIconButton
                iconName="calendar-date"
                label={t('core.fields.date.selectDate')}
                action={() => setIsOpen(!isOpen)}
              />
            </PopoverTrigger>
          }
          placeholder={mask?.placeholder}
          onChange={(e) => {
            setDate(getDate(e.target.value));
          }}
          onVisible={() => {
            loadMask();
            loadPattern();
          }}
          {...(!!mask && { maskOptions: mask.options })}
          pattern={
            pattern
              ? {
                  value: pattern,
                  message: t('core.forms.errors.invalidDateFieldFormat'),
                }
              : undefined
          }
          validate={{
            minDaysOut: () => {
              const isoDate = toIsoDateString(date);
              const isoFirstAllowedDate = toIsoDateString(firstAllowedDate);

              return !isoDate ||
                !isoFirstAllowedDate ||
                isoDate >= isoFirstAllowedDate
                ? true
                : t('core.forms.errors.firstAllowedDate', {
                    minDaysOut,
                  });
            },
            validDate: () =>
              isValidDate(date)
                ? true
                : t('core.forms.errors.invalidDateFieldValue'),
          }}
          {...inputFieldProps}
        />

        <PopoverContent
          className="DateField__content"
          align="end"
          sideOffset={2}
          avoidCollisions={false}
        >
          <Calendar
            className="DateField__calendar"
            mode="single"
            locale={currentLocale}
            selected={date}
            disabled={disableMatchers}
            month={month}
            startMonth={firstAllowedDate}
            endMonth={endDate}
            onMonthChange={setMonth}
            onSelect={(selectedDate) => {
              setDate(selectedDate);
              setValue(formatDate(selectedDate, currentLocale));
              setIsOpen(false);
            }}
            autoFocus={false}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};
